import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, Button, Alert, Tabs, Tab } from '@mui/material';
import { Code } from '@mui/icons-material';
import Editor from '@monaco-editor/react';

function NextJsWorkspace({ question, onCodeChange, initialCode = '' }) {
  const defaultCode = `import { useState } from 'react';

export default function Home() {
  const [count, setCount] = useState(0);

  return (
    <div style={{ padding: '20px', fontFamily: 'Arial' }}>
      <h1>Next.js Page</h1>
      <p>Count: {count}</p>
      <button onClick={() => setCount(count + 1)}>
        Increment
      </button>

      <style jsx>{\`
        button {
          padding: 10px 20px;
          font-size: 16px;
          cursor: pointer;
          margin-top: 10px;
        }
      \`}</style>
    </div>
  );
}

// Optional: Server-side props
export async function getServerSideProps(context) {
  return {
    props: {}, // will be passed to the page component as props
  };
}`;

  const [code, setCode] = useState(initialCode || defaultCode);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    if (onCodeChange) {
      onCodeChange(code);
    }
  }, [code, onCodeChange]);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {question && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            {question.name || 'Next.js Challenge'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
            {question.task || question.question || question.text}
          </Typography>
        </Paper>
      )}

      <Alert severity="warning" sx={{ mb: 2 }}>
        <strong>Next.js Workspace</strong> - Next.js requires a Node.js environment for SSR/SSG features.
        Write your page component code here. Full preview requires Next.js development server.
      </Alert>

      <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="Page Component" />
        <Tab label="Instructions" />
      </Tabs>

      {activeTab === 0 && (
        <Box sx={{ flexGrow: 1, border: '1px solid #ddd', borderRadius: 1, overflow: 'hidden' }}>
          <Editor
            height="100%"
            defaultLanguage="javascript"
            path="pages/index.js"
            value={code}
            onChange={(value) => setCode(value || '')}
            theme="vs-dark"
            options={{
              minimap: { enabled: false },
              fontSize: 14,
              lineNumbers: 'on',
              automaticLayout: true,
              tabSize: 2,
            }}
          />
        </Box>
      )}

      {activeTab === 1 && (
        <Paper sx={{ flexGrow: 1, p: 3, overflow: 'auto' }}>
          <Typography variant="h6" gutterBottom>
            Next.js Development Guidelines
          </Typography>
          <Typography variant="body2" paragraph>
            Write your Next.js page component. You can include:
          </Typography>
          <ul>
            <li>Default exported React component (page component)</li>
            <li>Client-side state management with React hooks</li>
            <li>CSS-in-JS with styled-jsx or CSS modules</li>
            <li>Data fetching methods: getServerSideProps, getStaticProps, getStaticPaths</li>
            <li>API routes (if applicable)</li>
          </ul>
          <Typography variant="body2" paragraph sx={{ mt: 2 }}>
            Your code will be evaluated for:
          </Typography>
          <ul>
            <li>Proper Next.js page structure and conventions</li>
            <li>Correct use of data fetching methods</li>
            <li>React best practices and hooks usage</li>
            <li>Understanding of SSR/SSG concepts</li>
          </ul>
          <Typography variant="body2" paragraph sx={{ mt: 2, fontStyle: 'italic' }}>
            Note: This workspace provides code editing only. For full Next.js features including
            routing, API routes, and server-side rendering, a complete Next.js environment is required.
          </Typography>
        </Paper>
      )}

      <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
        <Button
          variant="outlined"
          startIcon={<Code />}
          onClick={() => setActiveTab(activeTab === 0 ? 1 : 0)}
        >
          {activeTab === 0 ? 'View Instructions' : 'Back to Code'}
        </Button>
      </Box>
    </Box>
  );
}

export default NextJsWorkspace;
